import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { UmsService } from '../ums.service';

@Component({
  selector: 'app-collegewise-student-name-list',
  templateUrl: './collegewise-student-name-list.component.html',
  styleUrls: ['./collegewise-student-name-list.component.scss']
})
export class CollegewiseStudentNameListComponent implements OnInit {
  reportForm: FormGroup;
  userPermission: any;
  userId: number | null = null;
  collegeList: any[] = [];
  filteredCollegeList: any[] = [];
  sessionList: any[] = [];
  SessionFilterList: any[] = [];
  isDownloading = false;
  selectedColleges: string[] = [];
  commaSeparatedColleges: string = '';
  showCollegewiseButton: boolean = false;
  showCenterwiseButton: boolean = false;

  constructor(
    public umsService: UmsService,
    private fb: FormBuilder,
    public router: Router,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.fetchSessions();
    this.userPermission = JSON.parse(sessionStorage.getItem('userPermission') || '[]');
    this.userId = this.userPermission.length ? this.userPermission[0].UserId : null;
    this.fetchColleges();
  }

  initializeForm(): void {
    this.reportForm = this.fb.group({
      SessionId: new FormControl(''),          // optional
      college: new FormControl([], Validators.required), // mandatory
      reportType: new FormControl('CLG'),
      fromDate: new FormControl(''),           // optional
      toDate: new FormControl('')              // optional
    }, {
      validators: this.dateRangeValidator
    });

    // // --- Session change: enable college and fetch colleges ---
    // this.reportForm.get('SessionId')?.valueChanges.subscribe((value: string) => {
    //   if (value) {
    //     this.reportForm.get('college')?.enable();
    //     this.fetchColleges(value);
    //     this.selectedColleges = [];
    //     this.commaSeparatedColleges = '';
    //     this.reportForm.get('college')?.setValue([]);
    //   } else {
    //     this.reportForm.get('college')?.disable();
    //     this.reportForm.get('college')?.setValue([]);
    //     this.collegeList = [];
    //     this.filteredCollegeList = [];
    //     this.selectedColleges = [];
    //     this.commaSeparatedColleges = '';
    //   }
    //   this.updateDownloadButtonVisibility();
    // });

    // College selection change
    this.reportForm.get('college')?.valueChanges.subscribe((value: string[]) => {
      if (value && value.length > 0) {
        this.selectedColleges = value;
        this.commaSeparatedColleges = value.join(',');
      } else {
        this.selectedColleges = [];
        this.commaSeparatedColleges = '';
      }
      this.updateDownloadButtonVisibility();
    });

    // Date changes (affect button visibility if we choose to, but we now ignore date requirement)
    this.reportForm.get('fromDate')?.valueChanges.subscribe(() => {
      this.updateDownloadButtonVisibility();
      this.reportForm.get('toDate')?.updateValueAndValidity();
    });

    this.reportForm.get('toDate')?.valueChanges.subscribe(() => {
      this.updateDownloadButtonVisibility();
      this.reportForm.get('fromDate')?.updateValueAndValidity();
    });
    this.reportForm.get('SessionId')?.valueChanges.subscribe(() => {
      this.updateDownloadButtonVisibility();
    });
  }

  // Custom validator for date range
  dateRangeValidator(group: FormGroup): any {
    const fromDate = group.get('fromDate')?.value;
    const toDate = group.get('toDate')?.value;

    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);

      if (from > to) {
        return { 'fromDateGreater': true };
      }
      
      if (to < from) {
        return { 'toDateLess': true };
      }
    }
    
    return null;
  }

  // Check if date range is valid
  isDateRangeValid(): boolean {
    const fromDate = this.reportForm.get('fromDate')?.value;
    const toDate = this.reportForm.get('toDate')?.value;
    
    if (!fromDate || !toDate) {
      return true;
    }
    
    const from = new Date(fromDate);
    const to = new Date(toDate);
    from.setHours(0, 0, 0, 0);
    to.setHours(0, 0, 0, 0);
    
    return from <= to;
  }

  // --- Fetch sessions ---
  fetchSessions(): void {
    this.spinner.show();
    this.umsService.getSession({}).subscribe((res: any) => {
      if (res.status) {
        this.sessionList = res.data.filter((item: any) => item.SnStatus === 'O');
        this.SessionFilterList = this.sessionList.slice();
      } else {
        this.sessionList = [];
        this.SessionFilterList = [];
      }
      this.spinner.hide();
    }, (error: HttpErrorResponse) => {
      console.error('Error fetching sessions:', error);
      this.spinner.hide();
    });
  }

  fetchColleges(sessionId?: string): void {
    this.spinner.show();
    const payload = sessionId ? { SessionID: sessionId } : {};

    this.umsService.getSessionColgCo(payload).subscribe((res: any) => {
      if (res.status && res.data) {
        const uniqueCollegesMap = new Map();
        res.data.forEach((item: any) => {
          const cgCode = item.CoCgCgCode ? item.CoCgCgCode.trim() : null;
          if (cgCode && !uniqueCollegesMap.has(cgCode)) {
            const cgName = item.CgName ? item.CgName.trim() : cgCode;
            uniqueCollegesMap.set(cgCode, {
              CgCode: cgCode,
              CgName: cgName
            });
          }
        });

        let collegeArray = Array.from(uniqueCollegesMap.values());

        const userCollege = this.userPermission[0]?.CgUi === 'C' ? this.userPermission[0].CgId : '';
        if (userCollege) {
          collegeArray = collegeArray.filter(col => col.CgCode === userCollege);
        }

        this.collegeList = collegeArray;
        this.filteredCollegeList = this.collegeList.slice();
        this.selectedColleges = [];
        this.commaSeparatedColleges = '';
        this.reportForm.get('college')?.setValue([]);

        if (this.collegeList.length === 1) {
          this.selectedColleges = [this.collegeList[0].CgCode];
          this.commaSeparatedColleges = this.selectedColleges.join(',');
          this.reportForm.get('college')?.setValue(this.selectedColleges);
        }
      } else {
        this.collegeList = [];
        this.filteredCollegeList = [];
        this.selectedColleges = [];
        this.commaSeparatedColleges = '';
        this.reportForm.get('college')?.setValue([]);
      }
      this.spinner.hide();
      this.updateDownloadButtonVisibility();
    }, (error: HttpErrorResponse) => {
      console.error('Error fetching colleges:', error);
      this.collegeList = [];
      this.filteredCollegeList = [];
      this.selectedColleges = [];
      this.commaSeparatedColleges = '';
      this.reportForm.get('college')?.setValue([]);
      this.spinner.hide();
      this.updateDownloadButtonVisibility();
    });
  }

  // --- UI helpers ---
  selectAllColleges(): void {
    if (this.isAllSelected()) {
      this.selectedColleges = [];
      this.reportForm.get('college')?.setValue([], { emitEvent: false });
      this.commaSeparatedColleges = '';
    } else {
      this.selectedColleges = this.filteredCollegeList.map(item => item.CgCode);
      this.reportForm.get('college')?.setValue(this.selectedColleges, { emitEvent: false });
      this.commaSeparatedColleges = this.selectedColleges.join(',');
    }
    this.updateDownloadButtonVisibility();
  }

  onCollegeSelect(event: any): void {
    let selected = event.value ? [...event.value] : [];
    selected = selected.filter((item: any) => item !== 'selectAll');
    this.selectedColleges = selected;
    this.reportForm.get('college')?.setValue(selected, { emitEvent: false });
    this.commaSeparatedColleges = this.selectedColleges.join(',');
    this.updateDownloadButtonVisibility();
  }

  isAllSelected(): boolean {
    return this.selectedColleges.length === this.filteredCollegeList.length && this.filteredCollegeList.length > 0;
  }

  isIndeterminate(): boolean {
    return this.selectedColleges.length > 0 && this.selectedColleges.length < this.filteredCollegeList.length;
  }

  downloadCollegewiseReport(): void {
    const collegeControl = this.reportForm.get('college');
    if (!collegeControl?.value || collegeControl.value.length === 0) {
      collegeControl?.markAsTouched();
      return;
    }

    this.isDownloading = true;
    const formData = this.reportForm.value;
    const collegeCodes = this.commaSeparatedColleges ||
      (Array.isArray(formData.college) ? formData.college.join(',') : formData.college);

    const reportType = formData.reportType || 'CLG';

    const payload1: any = {
      CgCode: collegeCodes,
      Type: reportType
    };
    if (formData.SessionId) {
      payload1.Session = formData.SessionId;
    }

    const filter = {
      LoginId: parseInt(sessionStorage.getItem('userId') || '0', 10),
      RptCode: 11,
      InstName: '',
      InstNo: this.umsService.secretdata?.['ClubPartTrans'] || 0,
      ShId: '',
      CgCode: '',
      RollNo: '',
      Cent: '',
      TnCode: '',
      EsId: 0,
      CsNo: '',
      PgBreak: '',
      StrId: '0',
      StrCode: '',
      RgNo: '',
      UC: '',
      Ledger: '',
      RegnRoll: '',
      DsCode: '',
      CoCode: '',
      SnNo: payload1.Session || '',
      Extra1: JSON.stringify(payload1),
      Mode: false
    };

    this.umsService.setData(JSON.stringify(filter));
    this.router.navigate(['hbsreports']);

    setTimeout(() => {
      this.isDownloading = false;
    }, 1000);
  }

  downloadCenterwiseReport(): void {
    const collegeControl = this.reportForm.get('college');
    if (!collegeControl?.value || collegeControl.value.length === 0) {
      collegeControl?.markAsTouched();
      return;
    }

    const fromDateValue = this.reportForm.get('fromDate')?.value;
    const toDateValue = this.reportForm.get('toDate')?.value;
    
    if (fromDateValue && toDateValue && !this.isDateRangeValid()) {
      this.reportForm.get('fromDate')?.setErrors({ 'fromDateGreater': true });
      this.reportForm.get('toDate')?.setErrors({ 'toDateLess': true });
      return;
    }

    this.isDownloading = true;
    const formData = this.reportForm.value;
    const collegeCodes = this.commaSeparatedColleges ||
      (Array.isArray(formData.college) ? formData.college.join(',') : formData.college);

    const fromDate = formData.fromDate ? this.formatDate(formData.fromDate) : null;
    const toDate = formData.toDate ? this.formatDate(formData.toDate) : null;

    const payload1: any = {
      CgCode: collegeCodes,
      Type: 'CENT',
      FromDate: fromDate,
      ToDate: toDate
    };
    if (formData.SessionId) {
      payload1.Session = formData.SessionId;
    }

    const filter = {
      LoginId: parseInt(sessionStorage.getItem('userId') || '0', 10),
      RptCode: 12,
      InstName: '',
      InstNo: this.umsService.secretdata?.['ClubPartTrans'] || 0,
      ShId: '',
      CgCode: '',
      RollNo: '',
      Cent: '',
      TnCode: '',
      EsId: 0,
      CsNo: '',
      PgBreak: '',
      StrId: '0',
      StrCode: '',
      RgNo: '',
      UC: '',
      Ledger: '',
      RegnRoll: '',
      DsCode: '',
      CoCode: '',
      SnNo: payload1.Session || '',
      Extra1: JSON.stringify(payload1),
      Mode: false
    };

    this.umsService.setData(JSON.stringify(filter));
    this.router.navigate(['hbsreports']);

    setTimeout(() => {
      this.isDownloading = false;
    }, 1000);
  }
//  public updateDownloadButtonVisibility(): void {
//     const formValues = this.reportForm.value;
//     const hasSessionAndCollege = !!(
//       formValues.SessionId &&
//       formValues.college &&
//       formValues.college.length > 0
//     );
    
//     this.showCollegewiseButton = hasSessionAndCollege;
    
//     const hasBothDates = !!(formValues.fromDate && formValues.toDate);
//     const isDateRangeValid = hasBothDates ? this.isDateRangeValid() : true;
    
//     this.showCenterwiseButton = hasSessionAndCollege && isDateRangeValid;
//   }

//   private markFormGroupTouched(formGroup: FormGroup): void {
//     Object.values(formGroup.controls).forEach(control => {
//       control.markAsTouched();
//       if (control instanceof FormGroup) {
//         this.markFormGroupTouched(control);
//       }
//     });
//   }
  public updateDownloadButtonVisibility(): void {
    const formValues = this.reportForm.value;
    const hasCollege = !!(formValues.college && formValues.college.length > 0);
    this.showCollegewiseButton = hasCollege;
    this.showCenterwiseButton = hasCollege;
  }

  private formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  }

  getCollegeName(collegeCode: string): string {
    const college = this.collegeList.find(c => c.CgCode === collegeCode);
    return college ? college.CgName : collegeCode;
  }

  getSessionName(sessionId: string): string {
    const session = this.sessionList.find(s => s.SnNo === sessionId);
    return session ? session.SnName : sessionId;
  }
}